import { SelectableOptions } from 'commons/components/searchable-dropdown/searchable-dropdown.type';
import { debounce } from 'commons/utils/functions.utils';
import { uniqBy } from 'commons/utils/object.utils';
import { DEFAULT_FILTER_SEARCH_DEBOUNCE } from 'core/constants/constants';
import { usePocQuery } from 'core/custom-hooks/use-poc-query.hook';
import { executeRequest } from 'core/utils/backend-service';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface UseSearchTableFilterProps<T, TCriteria> {
    queryKey: readonly unknown[];
    initialSearch?: string;
    selectedIds?: string[];
    criteria: (searchValue: string) => TCriteria;
    idCriteria?: (ids: string[]) => TCriteria;
    fetchFunction: (criteria: TCriteria) => Promise<T[]>;
    getOptionValue: (item: T) => string;
    getOptionId?: (item: T) => string;
    getSecondaryText?: (item: T) => string | undefined;
    onApplySelection: (selectedValues: string[]) => void;
}

export const useSearchTableFilter = <T, TCriteria>({
    queryKey,
    initialSearch = '',
    selectedIds = [],
    criteria,
    idCriteria,
    fetchFunction,
    getOptionValue,
    getOptionId,
    getSecondaryText,
    onApplySelection,
}: UseSearchTableFilterProps<T, TCriteria>) => {
    const [selectedValue, setSelectedValue] = useState<SelectableOptions[]>([]);
    const [search, setSearch] = useState<string>(initialSearch);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [options, setOptions] = useState<SelectableOptions[]>([]);

    const dropdownValue = selectedValue.map((option) => option.id);

    const mapToOptions = useCallback(
        (items: T[]): SelectableOptions[] =>
            items.map((item) => {
                const option: SelectableOptions = {
                    id: getOptionId?.(item) ?? getOptionValue(item),
                    name: getOptionValue(item),
                };

                const secondary = getSecondaryText?.(item);
                if (secondary) {
                    option.secondaryText = secondary;
                }

                return option;
            }),
        []
    );

    const selectedIdsKey = selectedIds.join(',');
    const [labelledIdsKey, setLabelledIdsKey] = useState<string>();

    // Resolve the labels of the pre-selected ids (mirrors the legacy selectedIds effect).
    const { data: resolvedSelected, isFetching: isSelectedLoading } = usePocQuery({
        queryKey: [...queryKey, 'selected', idCriteria ? idCriteria(selectedIds) : selectedIds],
        queryFn: () => executeRequest(() => fetchFunction(idCriteria!(selectedIds))),
        enabled: selectedIds.length > 0 && !!idCriteria && labelledIdsKey !== selectedIdsKey,
        select: mapToOptions,
        staleTime: 0,
        // Single attempt, matching the legacy one-shot fetch: executeRequest already
        // toasts on failure, so the global retry would surface a duplicate error toast.
        retry: false,
    });

    // Typeahead search; the full criteria object is part of the key so any change
    // to the criteria (active flag, field, ...) refetches, matching the legacy always-fresh fetch.
    const { data: resolvedSearch, isFetching: isSearchLoading } = usePocQuery({
        queryKey: [...queryKey, 'search', criteria(searchTerm)],
        queryFn: () => executeRequest(() => fetchFunction(criteria(searchTerm))),
        enabled: searchTerm.length > 0,
        select: mapToOptions,
        staleTime: 0,
        // Single attempt, matching the legacy one-shot fetch (see the selected query above).
        retry: false,
    });

    const isLoading = isSelectedLoading || isSearchLoading;

    const [appliedIdsKey, setAppliedIdsKey] = useState<string>(selectedIdsKey);

    if (appliedIdsKey !== selectedIdsKey) {
        const applied = selectedValue.filter((option) => selectedIds.includes(option.id));
        const keptSelection = applied.length === selectedValue.length ? selectedValue : applied;
        const isEveryAppliedIdLabelled = selectedIds.every((id) => applied.some((option) => option.id === id));
        setAppliedIdsKey(selectedIdsKey);
        if (isEveryAppliedIdLabelled) {
            setLabelledIdsKey(selectedIdsKey);
        }
        setSelectedValue(keptSelection);
        setOptions(keptSelection);
    }

    useEffect(() => {
        if (resolvedSelected) {
            setSelectedValue(resolvedSelected);
            setOptions(resolvedSelected);
        }
    }, [resolvedSelected]);

    useEffect(() => {
        if (resolvedSearch) {
            setOptions(resolvedSearch);
        }
    }, [resolvedSearch]);

    const debouncedSetSearchTerm = useMemo(
        () => debounce((value: string) => setSearchTerm(value), DEFAULT_FILTER_SEARCH_DEBOUNCE),
        []
    );

    const handleOnChangeSearchValue = (value: string) => {
        setSearch(value);
        debouncedSetSearchTerm(value);

        if (value.length === 0) {
            setOptions(selectedValue);
        }
    };

    const onValueChange = (value: string[]) => {
        setSelectedValue((prev) =>
            uniqBy(
                [...prev.filter((op) => value.includes(op.id)), ...options.filter((op) => value.includes(op.id))],
                (option) => option.id
            )
        );
    };

    const handleApply = () => {
        onApplySelection(selectedValue.map((option) => option.id));
    };

    const onClear = () => {
        setSelectedValue([]);
        setOptions([]);
        setSearch('');
        setSearchTerm('');
        onApplySelection([]);
    };

    const customFilter = {
        filter: search,
        selectedOptions: selectedValue,
        onChangeFilter: handleOnChangeSearchValue,
        onExecuteSearch: () => setSearchTerm(search),
    };

    return {
        options,
        isLoading,
        dropdownValue,
        selectedValue,
        customFilter,
        handleApply,
        onValueChange,
        onClear,
    };
};
